import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { PlusCircle } from 'lucide-react';
import PropTypes from 'prop-types';

import API from '@/apiRequest';
import FeatureSwitch from '@/components/common/FeatureSwitch';
import DeleteAttachment from '@/components/DeleteAttachment';
import { showToast } from '@/store/ducks/toasts';
import AttachIcon from '@/svg-icons/attachments';
import classes from './Attachments.module.scss';

const Attachments = ({ canAdd, data, orderId }) => {
  const [attachments, setAttachments] = useState(data || []);
  const [loading, setLoading] = useState(false);
  const intl = useIntl();
  const dispatch = useDispatch();

  useEffect(() => {
    setAttachments(data || []);
  }, [data]);

  const openFileHandler = async (filename, location) => {
    try {
      const res = await API.get(`/orders/${orderId}/attachments`, {
        params: { filename, location },
      });
      window.open(res.data.url, '_blank');
    } catch (err) {
      console.log(err);
    }
  };

  const notifyFileUploadHandler = async (remoteName, originalName, extension) => {
    try {
      const res = await API.post(`/orders/${orderId}/attachments`, {
        original_name: originalName,
        remote_name: remoteName,
        extension,
      });
      setAttachments(res.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fileUploadHandler = async (e) => {
    try {
      setLoading(true);
      const file = e.target.files[0];

      if (file?.type !== 'application/pdf') {
        dispatch(
          showToast({
            type: 'error',
            title: intl.formatMessage({ id: 'ERROR' }),
            text: intl.formatMessage({ id: 'ATTACH_ONLY_PDF_FILES' }),
            duration: 5000,
          })
        );
        throw new Error('Invalid file type');
      }

      const originalName = file.name;
      const fileExtension = file.name.split('.').pop();
      const res = await API.get('/upload');
      
      const response = await fetch(res.data.url, {
        method: 'PUT',
        contentType: 'binary/octet-stream',
        body: file,
      });

      if (response.ok) {
        notifyFileUploadHandler(res.data.remote_name, originalName, fileExtension);
      }
    } catch (err) {
      setLoading(false);
      console.debug('Attachments.fileUploadHandler error ', err);
    }
  };

  const onDelete = remoteName => {
    setAttachments(prev => prev.filter(att => att.remote_name !== remoteName));
  };

  if (canAdd === false && attachments.length < 1) {
    return null;
  }

  const files = attachments.map(attachment => (
    <li key={attachment.remote_name}>
      <button
        type="button"
        className={classes.link}
        onClick={() => openFileHandler(attachment.remote_name, attachment.location)}
        data-cy="order-details-attachment-original-name-button"
      >
        {attachment.original_name}
      </button>

      <FeatureSwitch feature="DELETE_ATTACHMENT">
        <DeleteAttachment orderId={orderId} remoteName={attachment.remote_name} onDelete={onDelete} />
      </FeatureSwitch>
    </li>
  ));

  let buttonAdd = (
    <p>
      <input
        type="file"
        accept="application/pdf"
        id="attachment-upload"
        style={{ display: 'none' }}
        onChange={fileUploadHandler}
        data-cy="order-details-attachment-upload-input"
      />

      <label
        htmlFor="attachment-upload"
        className={classes.addButton}
      >
        <PlusCircle />
        <FormattedMessage id="ADD" />
      </label>
    </p>
  );

  if (loading) {
    buttonAdd = (
      <p>
        <FormattedMessage id="sending" />
      </p>
    );
  }

  return (
    <div>
      <div className={classes.header}>
        <AttachIcon viewBox="0 0 34.016 34.016" className={classes.icon} />
        <FormattedMessage id="attachments" />
      </div>

      <div className={classes.information}>
        <ol>{files}</ol>
        {canAdd ? buttonAdd : null}
      </div>
    </div>
  );
};

Attachments.propTypes = {
  canAdd: PropTypes.bool.isRequired,
  data: PropTypes.array,
  orderId: PropTypes.string.isRequired,
};

Attachments.defaultProps = {
  data: [],
};

export default Attachments;
